import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import { AuthProvider } from './contexts/AuthContext';
import { SessionProvider } from './contexts/SessionContext';
import SessionWarningModal from './components/session/SessionWarningModal';
import LockScreenOverlay from './components/session/LockScreenOverlay';
import { LayoutProvider } from './contexts/LayoutContext';
import { PeekNotificationProvider } from './contexts/PeekNotificationContext';
import NotFound from "pages/NotFound";
import ProtectedRoute from './components/ProtectedRoute';
import RoleBasedRedirect from './components/RoleBasedRedirect';
import HelpSystem from './components/help/HelpSystem';

// Eager imports — first pages users see (login flow)
import Login from './pages/login';
import ForgotPassword from './pages/forgot-password';
import AuthenticationPortal from './pages/authentication-portal';

// Lazy imports — all other pages are code-split into separate chunks
const DoctorDashboard = lazy(() => import('./pages/doctor-dashboard'));
const PatientRegistration = lazy(() => import('./pages/patient-registration'));
const LaboratoryDashboard = lazy(() => import('./pages/laboratory-dashboard'));
const LaboratoryInventoryPage = lazy(() => import('./pages/laboratory-inventory'));
const LabProcessingPage = lazy(() => import('./pages/lab-processing'));
const PatientRecords = lazy(() => import('./pages/patient-records'));
const PharmacyDashboard = lazy(() => import('./pages/pharmacy-dashboard'));
const PharmacyPrescriptionsPage = lazy(() => import('./pages/pharmacy-prescriptions'));
const PharmacyInventoryPage = lazy(() => import('./pages/pharmacy-inventory'));
const PharmacyInteractionsPage = lazy(() => import('./pages/pharmacy-interactions'));
const PharmacyReportsPage = lazy(() => import('./pages/pharmacy-reports'));
// DEPRECATED: PrescriptionManagement — replaced by IntegratedConsultationWorkspace
// const PrescriptionManagement = lazy(() => import('./pages/prescription-management'));
const NursingDashboard = lazy(() => import('./pages/nursing-dashboard'));
const ConsultationNotesScreen = lazy(() => import('./pages/consultation-notes-screen'));
const IntegratedConsultationWorkspace = lazy(() => import('./pages/consultation-notes-screen/IntegratedConsultationWorkspace'));
const UserAdministrationDashboard = lazy(() => import('./pages/user-administration-dashboard'));
const AdminDashboard = lazy(() => import('./pages/admin-dashboard'));
const UserCreationInterface = lazy(() => import('./pages/user-creation-interface'));
const AcceptInvitePage = lazy(() => import('./pages/accept-invite'));
const ProfileSetupPage = lazy(() => import('./pages/profile-setup'));
const UserProfilePage = lazy(() => import('./pages/user-profile'));
const AccountsDashboard = lazy(() => import('./pages/accounts-dashboard'));
const AccountsDischargesPage = lazy(() => import('./pages/accounts-discharges'));
const AccountsConsultationPage = lazy(() => import('./pages/accounts-consultation'));
const AccountsLabPage = lazy(() => import('./pages/accounts-lab'));
const AccountsMedicationPage = lazy(() => import('./pages/accounts-medication'));
const AccountsInvoicesPage = lazy(() => import('./pages/accounts-invoices'));
const ServiceManagement = lazy(() => import('./pages/service-management'));
const FacilityManagement = lazy(() => import('./pages/admin-dashboard/components/FacilityManagement'));
const BedManagementPage = lazy(() => import('./pages/nursing-dashboard/components/BedManagementPage'));
// Patient Self-Registration (public)
const PatientSelfRegister = lazy(() => import('./pages/patient-self-register'));
const PatientRegisterForm = lazy(() => import('./pages/patient-register-form'));
const HospitalConfiguration = lazy(() => import('./pages/hospital-configuration'));
const PulseDashboard = lazy(() => import('./pages/pulse-dashboard'));
const DutyRosterDashboard = lazy(() => import('./pages/duty-roster'));
const PatientFullView = lazy(() => import('./pages/patient-full-view'));
const MedicationSchedulePage = lazy(() => import('./pages/medication-schedule'));
const ShiftHandoverPage = lazy(() => import('./pages/shift-handover'));
const NotificationsDashboard = lazy(() => import('./pages/notifications'));
const AncManagementPage = lazy(() => import('./pages/anc-management'));

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AuthProvider>
          <SessionProvider>
            <SessionWarningModal />
            <LockScreenOverlay />
            <LayoutProvider>
              <PeekNotificationProvider>
                <ScrollToTop />
              <Suspense fallback={
              <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-pulse text-muted-foreground">Loading...</div>
              </div>
            }>
              <HelpSystem>
                <RouterRoutes>
                {/* Public Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/accept-invite" element={<AcceptInvitePage />} />
                <Route path="/authentication-portal" element={<AuthenticationPortal />} />
                {/* Patient Self-Registration (Public) */}
                <Route path="/patient-self-register" element={<PatientSelfRegister />} />
                <Route path="/patient-register-form" element={<PatientRegisterForm />} />

                {/* Role-Based Redirect - Default Route */}
                <Route path="/" element={<RoleBasedRedirect />} />
                <Route path="/doctor-dashboard" element={
                  <ProtectedRoute requiredRoles={['doctor', 'admin']}>
                    <DoctorDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/patient-registration" element={
                  <ProtectedRoute requiredPermission={{ module: 'patients', action: 'view' }}>
                    <PatientRegistration />
                  </ProtectedRoute>
                } />
                <Route path="/laboratory-dashboard" element={
                  <ProtectedRoute requiredPermission={{ module: 'lab_tests', action: 'view' }}>
                    <LaboratoryDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/lab-processing" element={
                  <ProtectedRoute requiredPermission={{ module: 'lab_tests', action: 'view' }}>
                    <LabProcessingPage />
                  </ProtectedRoute>
                } />
                <Route path="/laboratory/inventory" element={
                  <ProtectedRoute requiredPermission={{ module: 'lab_tests', action: 'view' }}>
                    <LaboratoryInventoryPage />
                  </ProtectedRoute>
                } />
                <Route path="/laboratory-inventory" element={
                  <ProtectedRoute requiredPermission={{ module: 'lab_tests', action: 'view' }}>
                    <LaboratoryInventoryPage />
                  </ProtectedRoute>
                } />
                <Route path="/patient-records" element={
                  <ProtectedRoute requiredPermission={{ module: 'patients', action: 'view' }}>
                    <PatientRecords />
                  </ProtectedRoute>
                } />
                <Route path="/patient-full-view/:patientId" element={
                  <ProtectedRoute requiredPermission={{ module: 'patients', action: 'view' }}>
                    <PatientFullView />
                  </ProtectedRoute>
                } />
                 <Route path="/pharmacy-dashboard" element={
                  <ProtectedRoute requiredPermission={{ module: 'pharmacy', action: 'view' }}>
                    <PharmacyDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/pharmacy/prescriptions" element={
                  <ProtectedRoute requiredPermission={{ module: 'pharmacy', action: 'view' }}>
                    <PharmacyPrescriptionsPage />
                  </ProtectedRoute>
                } />
                <Route path="/pharmacy/inventory" element={
                  <ProtectedRoute requiredPermission={{ module: 'pharmacy', action: 'view' }}>
                    <PharmacyInventoryPage />
                  </ProtectedRoute>
                } />
                <Route path="/pharmacy-inventory" element={
                  <ProtectedRoute requiredPermission={{ module: 'pharmacy', action: 'view' }}>
                    <PharmacyInventoryPage />
                  </ProtectedRoute>
                } />
                <Route path="/pharmacy/interactions" element={
                  <ProtectedRoute requiredPermission={{ module: 'pharmacy', action: 'view' }}>
                    <PharmacyInteractionsPage />
                  </ProtectedRoute>
                } />
                <Route path="/pharmacy/reports" element={
                  <ProtectedRoute requiredPermission={{ module: 'pharmacy', action: 'view' }}>
                    <PharmacyReportsPage />
                  </ProtectedRoute>
                } />
                {/* DEPRECATED: /prescription-management redirects to /consultation-workspace */}
                <Route path="/prescription-management" element={
                  <Navigate to="/consultation-workspace" replace />
                } />
                <Route path="/nursing-dashboard" element={
                  <ProtectedRoute requiredRoles={['nurse', 'matron', 'admin']} requiredPermission={{ module: 'nursing', action: 'view' }}>
                    <NursingDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/anc-management" element={
                  <ProtectedRoute requiredRoles={['nurse', 'matron', 'doctor', 'admin']}>
                    <AncManagementPage />
                  </ProtectedRoute>
                } />
                <Route path="/consultation-notes-screen" element={
                  <ProtectedRoute requiredRoles={['doctor', 'admin']}>
                    <ConsultationNotesScreen />
                  </ProtectedRoute>
                } />
                <Route path="/consultation-workspace" element={
                  <ProtectedRoute requiredRoles={['doctor', 'admin']}>
                    <IntegratedConsultationWorkspace />
                  </ProtectedRoute>
                } />
                <Route path="/admin-dashboard" element={
                  <ProtectedRoute requiredPermission={{ module: 'user_administration', action: 'view' }}>
                    <AdminDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/user-administration-dashboard" element={
                  <ProtectedRoute requiredPermission={{ module: 'user_administration', action: 'view' }}>
                    <UserAdministrationDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/user-creation-interface" element={
                  <ProtectedRoute requiredPermission={{ module: 'user_administration', action: 'view' }}>
                    <UserCreationInterface />
                  </ProtectedRoute>
                } />
                <Route path="/profile-setup" element={
                  <ProtectedRoute>
                    <ProfileSetupPage />
                  </ProtectedRoute>
                } />
                <Route path="/user-profile" element={
                  <ProtectedRoute>
                    <UserProfilePage />
                  </ProtectedRoute>
                } />
                <Route path="/accounts-dashboard" element={
                  <ProtectedRoute requiredPermission={{ module: 'billing', action: 'view' }}>
                    <AccountsDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/accounts/discharges" element={
                  <ProtectedRoute requiredPermission={{ module: 'billing', action: 'view' }}>
                    <AccountsDischargesPage />
                  </ProtectedRoute>
                } />
                <Route path="/accounts/consultation" element={
                  <ProtectedRoute requiredPermission={{ module: 'billing', action: 'view' }}>
                    <AccountsConsultationPage />
                  </ProtectedRoute>
                } />
                <Route path="/accounts/lab" element={
                  <ProtectedRoute requiredPermission={{ module: 'billing', action: 'view' }}>
                    <AccountsLabPage />
                  </ProtectedRoute>
                } />
                <Route path="/accounts/medication" element={
                  <ProtectedRoute requiredPermission={{ module: 'billing', action: 'view' }}>
                    <AccountsMedicationPage />
                  </ProtectedRoute>
                } />
                <Route path="/accounts/invoices" element={
                  <ProtectedRoute requiredPermission={{ module: 'billing', action: 'view' }}>
                    <AccountsInvoicesPage />
                  </ProtectedRoute>
                } />
                <Route path="/service-management" element={
                  <ProtectedRoute requiredPermission={{ module: 'user_administration', action: 'view' }}>
                    <ServiceManagement />
                  </ProtectedRoute>
                } />
                <Route path="/hospital-configuration" element={
                  <ProtectedRoute requiredPermission={{ module: 'user_administration', action: 'view' }}>
                    <HospitalConfiguration />
                  </ProtectedRoute>
                } />
                <Route path="/facility-management" element={
                  <ProtectedRoute requiredPermission={{ module: 'user_administration', action: 'view' }}>
                    <FacilityManagement />
                  </ProtectedRoute>
                } />
                <Route path="/bed-management" element={
                  <ProtectedRoute requiredPermission={{ module: 'nursing', action: 'view' }}>
                    <BedManagementPage />
                  </ProtectedRoute>
                } />
                <Route path="/medication-schedule" element={
                  <ProtectedRoute requiredPermission={{ module: 'nursing', action: 'view' }}>
                    <MedicationSchedulePage />
                  </ProtectedRoute>
                } />
                <Route path="/shift-handover" element={
                  <ProtectedRoute requiredPermission={{ module: 'nursing', action: 'view' }}>
                    <ShiftHandoverPage />
                  </ProtectedRoute>
                } />
                <Route path="/pulse-dashboard" element={
                  <ProtectedRoute requiredPermission={{ module: 'user_administration', action: 'view' }}>
                    <PulseDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/duty-roster" element={
                  <ProtectedRoute requiredPermission={{ module: 'patients', action: 'view' }}>
                    <DutyRosterDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/notifications" element={
                  <ProtectedRoute>
                    <NotificationsDashboard />
                  </ProtectedRoute>
                } />
                <Route path="*" element={<NotFound />} />
              </RouterRoutes>
            </HelpSystem>
          </Suspense>
            </PeekNotificationProvider>
          </LayoutProvider>
          </SessionProvider>
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;